import { ValidationResultsSubscriber, ValidationEvent } from '@aurelia/validation-html';
import { ValidationResult } from '@aurelia/validation';
import { MouseButtonTracker } from '../browser-abstractions/mouse-button-tracker';
import { WindowAbstraction } from '../browser-abstractions/window-abstraction';

export class BootstrapFormRenderer implements ValidationResultsSubscriber {
  private _mouseButtonTracker: MouseButtonTracker;
  private _window: WindowAbstraction;

  constructor(mouseButtonTracker: MouseButtonTracker, window: WindowAbstraction) {
    this._mouseButtonTracker = mouseButtonTracker;
    this._window = window;
  }

  public handleValidationEvent(event: ValidationEvent): void {
    for (const { result, targets } of event.removedResults) {
      for (const element of targets) {
        this._remove(element, result);
      }
    }

    for (const { result, targets } of event.addedResults) {
      for (const element of targets) {
        this._add(element, result);
      }
    }
  }

  private _add(element: Element, result: ValidationResult): void {
    if (result.valid) {
      return;
    }

    // Hack: Vänta med valideringens rendering tills man släppt musknappen för att inte sparaknappen ska dansa iväg och man "missar" den.
    if (this._mouseButtonTracker.isMouseDown) {
      this._window.queue(() => this._add(element, result));
      return;
    }

    // Hack: För att rutor inte ska markeras röda efter att man tryckt på spara och därmed "blinka".
    const validationController = (result.object as any)?.validationController;
    if (validationController && Array.isArray(validationController.results)
      && !validationController.results.some((r: ValidationResult) => !r.valid)) {
      return;
    }

    this._closestPolyfill(element);

    const formInput = element.closest('.form-control');
    const formGroup = element.closest('.form-group');
    if (!formGroup) {
      return;
    }

    // add the has-error class to the enclosing form-group div
    formInput.classList.add('is-invalid');

    if (this._messageShouldBeHidden(element)) {
      return;
    }

    // add help-block
    const message = document.createElement('span');
    message.className = 'invalid-feedback';
    message.textContent = result.message;
    message.id = `validation-message-${result.propertyName}`;

    const childDiv = formGroup.querySelector(':scope > div');

    if (childDiv) {
      const span = childDiv.querySelector(':scope > span');
      if (span && span.textContent === result.message) {
        return;
      }
      childDiv.appendChild(message);
    } else {
      const span = formGroup.querySelector(':scope > span');
      if (span && span.textContent === result.message) {
        return;
      }
      formGroup.appendChild(message);
    }
  }

  private _remove(element: Element, result: ValidationResult): void {
    this._closestPolyfill(element);

    const formInput = element.closest('.form-control');
    const formGroup = element.closest('.form-group');
    if (!formGroup) {
      return;
    }
    // remove help-block
    let message = formGroup.querySelector(`#validation-message-${result.propertyName}`);
    if (message && message.parentNode === formGroup) {
      formGroup.removeChild(message);
    }

    const childDiv = formGroup.querySelector('div');

    if (childDiv) {
      message = childDiv.querySelector(`#validation-message-${result.propertyName}`);

      if (message) {
        childDiv.removeChild(message);
      }
    }

    formInput.classList.remove('is-invalid');
  }

  // Polyfill for Element.closest
  private _closestPolyfill(ELEMENT: any): void {
    ELEMENT.matches = ELEMENT.matches || ELEMENT.mozMatchesSelector || ELEMENT.oMatchesSelector || ELEMENT.webkitMatchesSelector;
    ELEMENT.closest = ELEMENT.closest ||
      function closest(selector: string): any {
        let element = this;

        while (element) {
          if (element.matches(selector)) {
            break;
          }
          element = element.parentElement;
        }
        return element;
      };
  }

  private _messageShouldBeHidden(element: Element): boolean {
    this._closestPolyfill(element);
    return !!element.closest('[data-validation-hide-message]');
  }
}
